import { useQuery } from '@tanstack/react-query';
import { ChannelIcon } from '../../../../components/ui/ChannelIcon';
import { formatStatsAsOf, METRIC_LABELS, type PostMetrics } from '../../../../lib/publishing/postStats';
import { PLATFORM_LABELS } from '../../../../lib/socialPostView';
import { PortalDialog } from './PortalDialog';
import { chipCaption } from './ClubCalendarChip';
import { socialApi, type ClientPost } from './socialApi';

// Performance of ONE of the club's own published posts. Opened from a calendar chip.
//
// Two honesty rules, both from the staff modal: the figures are a SNAPSHOT, so the
// as-of line is rendered whenever figures are (an absent caveat reads as live); and a
// metric the platform did not report is OMITTED, never shown as 0. Google Business has
// no post analytics at all, so it says so rather than showing zeros.
//
// Only Kyck-made posts reach here. A post published directly on the platform has no
// stats lookup (its id is positional), so the calendar renders no affordance on it.

function Metrics({ metrics }: { metrics: PostMetrics | null }) {
  const shown = metrics ? METRIC_LABELS.filter(({ key }) => metrics[key] !== null) : [];
  if (!metrics || shown.length === 0) {
    return <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">No figures reported for this post yet.</p>;
  }
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="stats-metrics">
      {shown.map(({ key, label }) => (
        <div key={key} className="rounded-lg border border-border px-3 py-2">
          <div className="text-lg font-semibold tabular-nums">{(metrics[key] as number).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      ))}
      {metrics.engagementRate !== null && (
        <div className="rounded-lg border border-border px-3 py-2">
          <div className="text-lg font-semibold tabular-nums">{metrics.engagementRate.toFixed(1)}%</div>
          <div className="text-xs text-muted-foreground">Engagement rate</div>
        </div>
      )}
    </div>
  );
}

function Note({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border border-border bg-muted px-3 py-2" role="status">
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

export function ClubPostStatsDialog({ post, onClose }: { post: ClientPost; onClose: () => void }) {
  const stats = useQuery({
    queryKey: ['social-post-stats', post.id],
    queryFn: () => socialApi.postStats(post.id),
    staleTime: 60_000,
    retry: false,
  });
  const data = stats.data;
  const media = post.media[0];
  const platforms = [...new Set(post.targets.map((t) => t.platform))];
  return (
    <PortalDialog title="Post performance" onClose={onClose} dismissOnOverlay testId="club-stats-dialog">
      <div className="space-y-4">
        <div className="overflow-hidden rounded-lg border border-border">
          {media && media.type !== 'video' && (
            <div className="flex max-h-[320px] w-full items-center justify-center bg-muted">
              <img src={media.thumbnailUrl ?? media.url} alt={media.altText ?? ''} className="max-h-[320px] w-full object-contain" />
            </div>
          )}
          <div className="p-3">
            <p className="whitespace-pre-wrap text-sm">{chipCaption(post.content)}</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {platforms.map((p) => (
                <span key={p} className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ChannelIcon channel={p} className="h-3.5 w-3.5" />
                  {PLATFORM_LABELS[p] ?? p}
                </span>
              ))}
              {data?.stats?.permalink && (
                <a href={data.stats.permalink} target="_blank" rel="noreferrer" className="ml-auto text-xs underline">
                  View on the platform
                </a>
              )}
            </div>
          </div>
        </div>

        {stats.isLoading && <p className="text-sm text-muted-foreground">Loading figures...</p>}
        {(stats.isError || data?.unavailable === 'error') && (
          <p role="alert" className="text-sm text-destructive">
            We could not load the figures for this post. Try again shortly.
          </p>
        )}
        {data?.unavailable === 'gbp-unsupported' && (
          <Note
            title="Figures are not available for Google Business posts"
            body="Google does not share post-level performance for Business Profile posts. This does not mean the post had no engagement."
          />
        )}
        {data?.unavailable === 'not-found' && (
          <Note title="No figures yet" body="This post has not been picked up yet. Figures usually appear within a day of publishing." />
        )}
        {data?.stats && (
          <>
            <Metrics metrics={data.stats.metrics} />
            {data.stats.platforms.length > 1 && (
              <div className="space-y-2" data-testid="stats-by-destination">
                <h3 className="text-sm font-semibold">By destination</h3>
                {data.stats.platforms.map((row) => (
                  <div key={row.platform} className="rounded-lg border border-border px-3 py-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <ChannelIcon channel={row.platform} className="h-4 w-4" />
                      {PLATFORM_LABELS[row.platform] ?? row.platform}
                      {!row.measurable && <span className="ml-auto text-xs font-normal text-muted-foreground">No analytics available</span>}
                    </div>
                    {row.measurable && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {row.metrics
                          ? METRIC_LABELS.filter(({ key }) => row.metrics![key] !== null)
                              .map(({ key, label }) => `${(row.metrics![key] as number).toLocaleString()} ${label.toLowerCase()}`)
                              .join(' · ')
                          : 'Not reported yet'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground" data-testid="stats-as-of">
              Figures are a snapshot from the last sync, not live: {formatStatsAsOf(data.stats.lastUpdated ?? data.stats.lastSync, new Date())}.
            </p>
          </>
        )}
      </div>
    </PortalDialog>
  );
}

/** Whether a calendar chip can open performance. Only a Kyck post that has actually gone out. */
export function canShowStats(origin: string, status: string): boolean {
  return origin === 'kyck' && (status === 'published' || status === 'partial');
}
